import { ChangeEvent, DragEvent, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  deletePortfolioItem,
  listAdminPortfolio,
  savePortfolioItem,
  uploadPortfolioImage,
  type AdminPortfolioItem,
} from '../../lib/admin-portfolio-api';
import { resolveStaticImage } from '../../lib/static-images';
import '../../styles/admin-content.css';
import '../../styles/admin-image-containment.css';

const createBlankItem = (sortOrder = 10): AdminPortfolioItem => ({
  id: '',
  title: 'New portfolio item',
  slug: '',
  category: 'Gel-X',
  caption: '',
  alt_text: '',
  image_path: null,
  image_url: '',
  starting_price: null,
  price_label: null,
  shape: null,
  length: null,
  finish: null,
  status: 'draft',
  is_featured: false,
  sort_order: sortOrder,
});

const slugify = (value: string) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `portfolio-${Date.now()}`;
const titleFromFilename = (name: string) => name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()).trim() || 'New portfolio image';
const isSupportedImage = (file: File) => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);

export default function AdminPortfolio() {
  const [items, setItems] = useState<AdminPortfolioItem[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState<AdminPortfolioItem>(createBlankItem());
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const selected = useMemo(() => items.find((item) => item.id === selectedId), [items, selectedId]);
  const metrics = useMemo(() => ({
    published: items.filter((item) => item.status === 'published').length,
    featured: items.filter((item) => item.status === 'published' && item.is_featured).length,
    drafts: items.filter((item) => item.status === 'draft').length,
    hidden: items.filter((item) => item.status === 'archived').length,
  }), [items]);

  useEffect(() => {
    listAdminPortfolio()
      .then((records) => {
        setItems(records);
        if (records[0]) {
          setSelectedId(records[0].id);
          setDraft(records[0]);
        }
      })
      .catch((error: Error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, []);

  function choose(item: AdminPortfolioItem) {
    setSelectedId(item.id);
    setDraft({ ...item });
    setMessage('');
  }

  function update<K extends keyof AdminPortfolioItem>(key: K, value: AdminPortfolioItem[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function uploadFiles(files: File[]) {
    const validFiles = files.filter(isSupportedImage);
    if (!validFiles.length) {
      setMessage('Choose JPG, PNG or WebP images only.');
      return;
    }
    const oversized = validFiles.find((file) => file.size > 8 * 1024 * 1024);
    if (oversized) {
      setMessage(`${oversized.name} is larger than 8 MB. Reduce it before uploading.`);
      return;
    }

    setUploading(true);
    setMessage(`Uploading ${validFiles.length} image${validFiles.length === 1 ? '' : 's'}…`);
    try {
      const startOrder = items.length ? Math.max(...items.map((item) => item.sort_order)) + 10 : 10;
      const created: AdminPortfolioItem[] = [];
      for (const [index, file] of validFiles.entries()) {
        const uploaded = await uploadPortfolioImage(file);
        const title = titleFromFilename(file.name);
        const saved = await savePortfolioItem({
          ...createBlankItem(startOrder + index * 10),
          title,
          slug: slugify(`${title}-${Date.now()}-${index}`),
          alt_text: `${title} nail design by MF_NAILEDIT`,
          image_path: uploaded.path,
          image_url: uploaded.url,
        });
        created.push(saved);
      }
      setItems((current) => [...current, ...created].sort((a, b) => a.sort_order - b.sort_order));
      choose(created[0]);
      setMessage(`${created.length} portfolio draft${created.length === 1 ? '' : 's'} uploaded. Add details, then publish when ready.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Portfolio upload failed.');
    } finally {
      setUploading(false);
      setDragging(false);
    }
  }

  function handleImage(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    void uploadFiles(files);
    event.target.value = '';
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    void uploadFiles(Array.from(event.dataTransfer.files));
  }

  async function persist(status: AdminPortfolioItem['status']) {
    if (!draft.title.trim()) {
      setMessage('Add a portfolio title before saving.');
      return;
    }
    if (status === 'published' && (!draft.image_url || !draft.alt_text.trim())) {
      setMessage('A photo and meaningful alt text are required before publishing.');
      return;
    }

    setSaving(true);
    setMessage(status === 'published' ? 'Publishing…' : status === 'archived' ? 'Hiding from the website…' : 'Saving draft…');
    try {
      const saved = await savePortfolioItem({
        ...draft,
        slug: draft.slug || slugify(draft.title),
        status,
        price_label: draft.starting_price === null ? null : `From ₦${draft.starting_price.toLocaleString('en-NG')}`,
      });
      setItems((current) => (
        current.some((item) => item.id === saved.id)
          ? current.map((item) => item.id === saved.id ? saved : item)
          : [...current, saved]
      ).sort((a, b) => a.sort_order - b.sort_order));
      setSelectedId(saved.id);
      setDraft(saved);
      setMessage(status === 'published' ? 'Portfolio image published successfully.' : status === 'archived' ? 'Portfolio image hidden from the public website.' : 'Portfolio draft saved privately.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save portfolio item.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!selected || !window.confirm(`Delete “${selected.title}”? This permanently removes its stored image and record.`)) return;
    setSaving(true);
    try {
      await deletePortfolioItem(selected);
      const remaining = items.filter((item) => item.id !== selected.id);
      setItems(remaining);
      const next = remaining[0] ?? createBlankItem();
      setSelectedId(next.id);
      setDraft(next);
      setMessage('Portfolio image deleted.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to delete portfolio image.');
    } finally {
      setSaving(false);
    }
  }

  return <AdminLayout title="Portfolio" description="Upload nail photos, add optional details and control exactly what appears publicly.">
    <section className="studio-admin-grid metrics">
      <article className="studio-admin-metric"><span>Published</span><strong>{loading ? '—' : metrics.published}</strong><small>Visible on the Portfolio page</small></article>
      <article className="studio-admin-metric"><span>Homepage features</span><strong>{loading ? '—' : metrics.featured}</strong><small>Published and featured</small></article>
      <article className="studio-admin-metric"><span>Drafts</span><strong>{loading ? '—' : metrics.drafts}</strong><small>Private while being prepared</small></article>
      <article className="studio-admin-metric"><span>Hidden</span><strong>{loading ? '—' : metrics.hidden}</strong><small>Stored but not public</small></article>
    </section>

    <section className={`portfolio-dropzone${dragging ? ' dragging' : ''}`} onDragOver={(event) => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={handleDrop}>
      <div><span>PORTFOLIO IMAGE UPLOAD</span><h2>Drop one or several nail photos here.</h2><p>Each image is compressed automatically and saved as a private draft. JPG, PNG and WebP are supported up to 8 MB each.</p></div>
      <label className="ac-primary">{uploading ? 'Uploading…' : 'Choose images'}<input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleImage} hidden disabled={uploading}/></label>
    </section>

    {message && <p className={message.toLowerCase().includes('unable') || message.toLowerCase().includes('failed') || message.toLowerCase().includes('larger') ? 'studio-admin-error' : 'studio-admin-feedback'} role="status">{message}</p>}

    <section className="ac-panel">
      <div className="ac-panel-head"><div><span>PORTFOLIO GALLERY</span><h2>{items.length} stored image{items.length === 1 ? '' : 's'}.</h2><p>Draft and hidden images stay private. Only published images appear on the website.</p></div></div>
      <div className="ac-list">{loading ? <p>Loading portfolio…</p> : items.length ? items.map((item) => <article key={item.id} className={selectedId === item.id ? 'selected' : ''} onClick={() => choose(item)}>
        <div className="ac-thumb">{item.image_url ? <img src={resolveStaticImage(item.image_url)} alt="" loading="lazy" decoding="async"/> : <><span>NO PHOTO</span><b>Upload required</b></>}</div>
        <div><h3>{item.title}</h3><p>{item.category} • {item.caption || 'Details optional'}</p></div>
        <div><p>{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'Saved record'}</p><small>{item.alt_text ? 'Alt text ready' : 'Alt text required to publish'}</small></div>
        <div><p>{item.is_featured ? 'Homepage featured' : 'Portfolio only'}</p><small>Sort order {item.sort_order}</small></div>
        <span className={`ac-badge ${item.status}`}>{item.status === 'published' ? 'Published' : item.status === 'archived' ? 'Hidden' : 'Draft'}</span>
        <button type="button" onClick={(event) => { event.stopPropagation(); choose(item); }}>Edit</button>
      </article>) : <p>No portfolio images yet. Upload the first photo above.</p>}</div>
    </section>

    <section className="ac-editor">
      <span>SELECTED PORTFOLIO IMAGE</span>
      <h2>{draft.title}</h2>
      <div className="ac-image-slot">{draft.image_url ? <img src={resolveStaticImage(draft.image_url)} alt={draft.alt_text || 'Portfolio preview'} loading="lazy" decoding="async"/> : <><small>IMAGE REQUIRED</small><strong>Upload a photo above</strong></>}</div>
      <label>Title<input value={draft.title} onChange={(event) => update('title', event.target.value)}/><small>Shown publicly on the portfolio card.</small></label>
      <label>Caption<textarea value={draft.caption} onChange={(event) => update('caption', event.target.value)}/><small>Optional description of the style, colour or finish.</small></label>
      <label>Alt text<input value={draft.alt_text} onChange={(event) => update('alt_text', event.target.value)}/><small>Required before publishing for accessibility.</small></label>
      <div className="ac-form-row">
        <label>Category<select value={draft.category} onChange={(event) => update('category', event.target.value)}><option>Gel-X</option><option>Acrylic</option><option>BIAB</option><option>Pedicure</option><option>Nail art</option><option>Natural nails</option><option>Press-ons</option></select><small>Used by public portfolio filters.</small></label>
        <label>Starting price<input inputMode="numeric" value={draft.starting_price ?? ''} onChange={(event) => update('starting_price', event.target.value ? Number(event.target.value.replace(/[^0-9]/g, '')) : null)}/><small>Optional. Leave blank for quote after review.</small></label>
      </div>
      <div className="ac-form-row"><label>Shape<input value={draft.shape ?? ''} onChange={(event) => update('shape', event.target.value || null)}/></label><label>Length<input value={draft.length ?? ''} onChange={(event) => update('length', event.target.value || null)}/></label></div>
      <div className="ac-form-row"><label>Finish<input value={draft.finish ?? ''} onChange={(event) => update('finish', event.target.value || null)}/></label><label>Sort order<input inputMode="numeric" value={draft.sort_order} onChange={(event) => update('sort_order', Number(event.target.value.replace(/[^0-9]/g, '')) || 0)}/></label></div>
      <label className="portfolio-feature-toggle"><input type="checkbox" checked={draft.is_featured} onChange={(event) => update('is_featured', event.target.checked)}/><span>Feature this published image on the homepage</span></label>
      <div className="ac-actions">
        <button className="ac-primary" type="button" onClick={() => persist('draft')} disabled={saving || uploading}>{saving ? 'Saving…' : 'Save draft'}</button>
        <button type="button" onClick={() => persist('published')} disabled={saving || uploading}>Publish</button>
        {selected && <button type="button" onClick={() => persist('archived')} disabled={saving || uploading}>Hide</button>}
        {selected && <button type="button" onClick={remove} disabled={saving || uploading}>Delete</button>}
      </div>
    </section>
  </AdminLayout>;
}
